<template>
  <v-container class="py-8" fluid>
    <!-- SKELETONS (animated shimmer) -->
    <template v-if="isLoading">
      <v-container max-width="1200">
        <v-row dense class="mb-6">
          <v-col cols="12" md="3" v-for="i in 4" :key="'sum'+i">
            <v-skeleton-loader type="card" rounded="lg" elevation="1" boilerplate loading class="mb-3" />
          </v-col>
        </v-row>

        <v-row dense class="mb-6">
          <v-col cols="12" md="6">
            <v-skeleton-loader type="paragraph, actions" rounded="lg" boilerplate loading />
          </v-col>
          <v-col cols="12" md="6">
            <v-skeleton-loader type="paragraph, actions" rounded="lg" boilerplate loading />
          </v-col>
        </v-row>

        <v-row dense>
          <v-col cols="12" v-for="i in 2" :key="'guest'+i">
            <v-skeleton-loader type="article, actions" rounded="lg" elevation="1" boilerplate loading class="mb-4" />
          </v-col>
        </v-row>
      </v-container>
    </template>

    <!-- CONTENT -->
    <v-container v-else max-width="1200">
      <h2 class="text-h4 font-weight-bold mb-6">Booking Form</h2>

      <!-- TOP SUMMARY -->
      <v-row class="mb-6" dense>
        <v-col cols="12" md="3" v-for="card in summaryCards" :key="card.title">
          <v-card variant="tonal" class="h-100">
            <v-card-title class="text-subtitle-1 font-weight-bold">{{ card.title }}</v-card-title>
            <v-card-text class="text-body-2">
              <template v-if="card.key==='suite'">
                <div><strong>Code:</strong> {{ currentListing.selected_code }}</div>
                <div><strong>Description:</strong> {{ currentListing.description }}</div>
                <div><strong>Room Type:</strong> {{ currentListing.room_type }}</div>
                <div><strong>Cabin Number:</strong> {{ currentListing.cabin_number || 'TBD' }}</div>
              </template>
              <template v-else-if="card.key==='price'">
                <div><strong>Cruise Fare:</strong> ${{ formatNumber(currentListing.cruise_fare) }}</div>
                <div><strong>Taxes:</strong> ${{ formatNumber(currentListing.taxes) }}</div>
                <div><strong>Service Fee:</strong> ${{ formatNumber(currentListing.platform_fee) }}</div>
                <div class="text-error"><strong>Total Price:</strong> ${{ formatNumber(currentListing.total_guest_price) }}</div>
              </template>
              <template v-else-if="card.key==='terms'">
                <div><strong>Required Deposit:</strong> ${{ formatNumber(currentListing.deposit) }}</div>
                <div><strong>Final Payment Date:</strong> {{ formatDate(currentListing.final_payment_date) }}</div>
              </template>
              <template v-else>
                <template v-if="hasCancellation">
                  <ul class="pa-0 ms-4">
                    <li v-for="(c, i) in currentListing.cancellation" :key="i">
                      {{ formatDate(c.date) || 'TBD' }} — {{ c.percentage || 'TBD' }}% penalty
                    </li>
                  </ul>
                </template>
                <div v-else class="text-medium-emphasis font-italic">No cancellation dates provided yet.</div>
              </template>
            </v-card-text>
          </v-card>
        </v-col>
      </v-row>

      <!-- FORM -->
      <v-form ref="formRef" v-model="formValid" validate-on="input" @submit.prevent="submitBooking" class="mb-8">
        <!-- GUEST COUNT -->
        <v-row class="mb-4" align="center">
          <v-col cols="12" md="6">
            <div class="d-flex align-center">
              <div class="text-subtitle-1 font-weight-semibold me-4">Number of Guests</div>
              <v-btn icon="mdi-minus" variant="outlined" size="small" :disabled="numGuests <= 1" @click="decreaseGuests" />
              <v-text-field v-model="numGuests" class="mx-2" style="max-width: 96px" type="number" readonly density="comfortable" variant="outlined" />
              <v-btn icon="mdi-plus" variant="outlined" size="small" :disabled="numGuests >= 2" @click="increaseGuests" />
            </div>
          </v-col>
        </v-row>

        <!-- GUESTS -->
        <v-row v-for="(guest, index) in guests" :key="index" class="mb-6">
          <v-col cols="12">
            <v-card elevation="1">
              <v-card-title class="d-flex align-center">
                <span class="text-subtitle-1 font-weight-bold">Guest {{ index + 1 }}</span>
                <v-chip
                  v-if="!guestIsComplete(guest)"
                  size="small"
                  color="warning"
                  class="ms-3"
                  label
                >Missing info</v-chip>
              </v-card-title>

              <v-card-text>
                <v-row dense>
                  <v-col cols="12" md="3">
                    <v-select
                      v-model="guest.title"
                      :items="['Dr','Master','Miss','Mr','Mrs','Ms']"
                      label="Title"
                      variant="outlined"
                      density="comfortable"
                      clearable
                    />
                  </v-col>
                  <v-col cols="12" md="3">
                    <v-text-field v-model="guest.firstName" :rules="[r.required]" label="First Name" variant="outlined" density="comfortable" />
                  </v-col>
                  <v-col cols="12" md="3">
                    <v-text-field v-model="guest.middleName" label="Middle Name" variant="outlined" density="comfortable" />
                  </v-col>
                  <v-col cols="12" md="3">
                    <v-text-field v-model="guest.lastName" :rules="[r.required]" label="Last Name" variant="outlined" density="comfortable" />
                  </v-col>

                  <v-col cols="12" md="3">
                    <div class="text-caption mb-1">Gender</div>
                    <v-btn-toggle v-model="guest.gender" color="primary" mandatory divided class="w-100">
                      <v-btn value="Male" text="Male" />
                      <v-btn value="Female" text="Female" />
                    </v-btn-toggle>
                    <v-messages
                      v-if="!guest.gender"
                      class="mt-1 text-error"
                      :value="['Required']"
                    />
                  </v-col>

                  <v-col cols="12" md="3">
                    <v-text-field
                      v-model="guest.dob"
                      :rules="[r.required]"
                      label="Date of Birth"
                      type="date"
                      variant="outlined"
                      density="comfortable"
                    />
                  </v-col>
                  <v-col cols="12" md="3">
                    <v-text-field v-model="guest.citizenship" :rules="[r.required]" label="Citizenship" variant="outlined" density="comfortable" />
                  </v-col>
                  <v-col cols="12" md="3">
                    <v-text-field v-model="guest.language" label="Language (optional)" variant="outlined" density="comfortable" />
                  </v-col>

                  <v-col cols="12" md="3">
                    <v-text-field v-model="guest.loyalty" label="Loyalty Number (optional)" variant="outlined" density="comfortable" />
                  </v-col>

                  <!-- Promo Qualifiers -->
                  <v-col cols="12">
                    <v-expansion-panels variant="accordion">
                      <v-expansion-panel>
                        <v-expansion-panel-title>
                          <v-icon icon="mdi-tag-multiple-outline" class="me-2" />
                          Promo Qualifiers
                        </v-expansion-panel-title>
                        <v-expansion-panel-text>
                          <v-row dense>
                            <v-col cols="12" md="3">
                              <v-text-field
                                v-model="guest.state"
                                label="State of Residency"
                                variant="outlined"
                                density="comfortable"
                              />
                            </v-col>
                            <v-col cols="12" md="9">
                              <div class="text-caption mb-2">Additional Qualifiers</div>
                              <v-checkbox
                                v-for="opt in additionalOptions"
                                :key="opt.value"
                                v-model="guest.selectedAdditional"
                                :label="opt.label"
                                :value="opt.value"
                                density="comfortable"
                                hide-details
                              />
                            </v-col>
                          </v-row>
                        </v-expansion-panel-text>
                      </v-expansion-panel>
                    </v-expansion-panels>
                  </v-col>
                </v-row>
              </v-card-text>
            </v-card>
          </v-col>
        </v-row>

        <!-- PREFERENCES -->
        <v-card class="mb-6" elevation="1">
          <v-card-title class="text-subtitle-1 font-weight-bold">Preferences</v-card-title>
          <v-card-text>
            <v-row dense>
              <v-col cols="12" md="6">
                <v-select
                  v-model="diningTime"
                  :items="['Early','Late','AnyTime']"
                  :rules="[r.required]"
                  label="Dining Time"
                  variant="outlined"
                  density="comfortable"
                  clearable
                />
              </v-col>
              <v-col cols="12" md="6">
                <v-select
                  v-model="bed"
                  :items="['Together','Apart']"
                  :rules="[r.required]"
                  label="Bed Configuration"
                  variant="outlined"
                  density="comfortable"
                  clearable
                />
              </v-col>
            </v-row>
          </v-card-text>
        </v-card>

        <!-- CONTACT -->
        <v-card class="mb-6" elevation="1">
          <v-card-title class="text-subtitle-1 font-weight-bold">Contact Information</v-card-title>
          <v-card-text>
            <v-row dense>
              <v-col cols="12" md="6">
                <v-text-field
                  v-model="contactPhone"
                  :rules="[r.required, r.phone]"
                  label="Phone Number"
                  type="tel"
                  variant="outlined"
                  density="comfortable"
                />
              </v-col>
              <v-col cols="12" md="6">
                <v-text-field
                  v-model="contactEmail"
                  :rules="[r.required, r.email]"
                  label="Email Address"
                  type="email"
                  variant="outlined"
                  density="comfortable"
                />
              </v-col>
            </v-row>
          </v-card-text>
        </v-card>

        <v-alert v-if="formError" type="error" variant="tonal" class="mb-4">
          {{ formError }}
        </v-alert>

        <div class="text-end">
          <v-btn color="primary" size="large" :loading="submitting" :disabled="!formValid" @click="onSubmit">
            Submit Booking
          </v-btn>
        </div>
      </v-form>
    </v-container>
  </v-container>
</template>

<script>
import { supabase } from '@/supabaseClient'
import { computed, ref, watch } from 'vue'
import { useStore } from 'vuex'

export default {
  components: {  },

  setup() {
    const store = useStore()
    const userData = computed(() => store.state.user)
    const contactPhone = ref('')
    const contactEmail = ref('')
    watch(userData, (u) => {
      if (u) {
        contactPhone.value = u.phone || ''
        contactEmail.value = u.email || ''
      }
    }, { immediate: true })
    return { userData, contactPhone, contactEmail }
  },

  data() {
    return {
      isLoading: true,
      submitting: false,
      formValid: false,
      formRef: null,

      cruiseId: null,
      currentListing: null,
      numGuests: 2,
      guests: [],
      diningTime: '',
      bed: '',
      additionalOptions: [
        { label: 'U.S. or Canadian Law Enforcement', value: 'law' },
        { label: 'Fire Department or EMT', value: 'fire' },
        { label: 'U.S. Military or Canadian Forces', value: 'military' },
      ],
      formError: '',

      r: {
        required: v => (!!v || v === 0) || 'Required',
        email: v => !v || /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(v) || 'Invalid email',
        phone: v => !v || /^[0-9+\-() ]{7,}$/.test(v) || 'Invalid phone'
      }
    }
  },

  computed: {
    hasCancellation() {
      return this.currentListing?.cancellation?.some(c => c.date || c.percentage)
    },
    summaryCards() {
      return [
        { title: 'Suite Details', key: 'suite' },
        { title: 'Price Summary', key: 'price' },
        { title: 'Payment Terms', key: 'terms' },
        { title: 'Cancellation Policy', key: 'cancel' }
      ]
    }
  },

  watch: {
    numGuests(newVal) {
      const count = parseInt(newVal)
      if (count > this.guests.length) {
        for (let i = this.guests.length; i < count; i++) {
          this.guests.push({
            title: '', firstName: '', middleName: '', lastName: '',
            dob: '', state: '', language: '', gender: '', citizenship: '',
            selectedAdditional: []
          })
        }
      } else {
        this.guests.splice(count)
      }
    }
  },

  async mounted() {
    this.cruiseId = this.$route.params.id
    // pre-seed guests so skeletons switch seamlessly
    this.guests = new Array(this.numGuests).fill(0).map(() => ({
      title: '', firstName: '', middleName: '', lastName: '',
      dob: '', state: '', language: '', gender: '', citizenship: '',
      selectedAdditional: []
    }))
    await this.getCruise()
    this.isLoading = false
  },

  methods: {
    guestIsComplete(g) {
      return g.firstName && g.lastName && g.gender && g.dob && g.citizenship
    },

    async getCruise() {
      try {
        const { data, error } = await supabase
          .from('listings')
          .select('*')
          .eq('id', this.cruiseId)
          .single()
        if (error) throw error
        this.currentListing = data
      } catch (err) {
        console.error('Error fetching listing:', err)
      }
    },

    increaseGuests() { if (this.numGuests < 2) this.numGuests++ },
    decreaseGuests() { if (this.numGuests > 1) this.numGuests-- },

    async onSubmit() {
      // validate once before submit for snappy UX
      const ok = await this.$refs.formRef?.validate()
      if (!ok) return
      await this.submitBooking()
    },

    async submitBooking() {
      this.formError = ''
      this.submitting = true

      try {
        const { data: { session }, error: sessionError } = await supabase.auth.getSession()
        if (sessionError || !session?.user) {
          this.formError = 'Unable to retrieve session. Please log in again.'
          this.submitting = false
          return
        }

        // final manual guard (keeps your old checks)
        for (let i = 0; i < this.guests.length; i++) {
          const g = this.guests[i]
          if (!this.guestIsComplete(g)) {
            this.formError = `Please complete all required fields for Guest ${i + 1}.`
            this.submitting = false
            return
          }
        }
        if (!this.diningTime || !this.bed || !this.contactPhone || !this.contactEmail) {
          this.formError = 'Please complete all required fields.'
          this.submitting = false
          return
        }

        const bookingPayload = {
          user_id: session.user.id,
          listing_id: this.currentListing.id,
          guests: this.guests,
          dining_time: this.diningTime,
          bed: this.bed,
          phone: this.contactPhone,
          email: this.contactEmail
        }

        const { error: insertError } = await supabase.from('bookings').insert([bookingPayload])
        if (insertError) throw insertError

        await supabase.from('listings').update({ status: 'bookingpending' }).eq('id', this.currentListing.id)

        this.$emit?.('submitted')
        alert('Booking submitted successfully!')
      } catch (e) {
        console.error(e)
        this.formError = 'There was an error submitting your booking. Please try again.'
      } finally {
        this.submitting = false
      }
    },

    formatDate(date) {
      if (!date) return null
      return new Date(date).toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' })
    },
    formatNumber(val) {
      if (val == null) return 'TBD'
      return Number(val).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })
    }
  }
}
</script>

<style scoped>
.font-italic { font-style: italic; }
</style>
